/**
 * pdfKindHeuristic — Heurística determinística de tipo de documento
 *
 * Lê as primeiras ~1500 chars de texto extraído de um PDF e procura
 * palavras-chave que indiquem se é um material empírico (voto judicial,
 * prontuário médico, transcrição de entrevista) em vez de uma referência
 * bibliográfica clássica.
 *
 * NÃO usa IA. NÃO faz chamada externa. NÃO é decisão final — apenas
 * retorna uma SUGESTÃO. O usuário sempre confirma manualmente no painel
 * de desambiguação. (Pilar 5: o pesquisador decide.)
 *
 * Usado por importQueueStore.analyzePdfs após detectDoiLightweight()
 * falhar (sem DOI nem ISBN), para sugerir o destino do upload.
 *
 * Tarefa 7.C — Sprint 6.5 (07/abr/2026).
 */

export type DetectedKind =
  | 'voto-judicial'
  | 'prontuario-medico'
  | 'transcricao-entrevista'
  | null;

export type SuggestedDestination = 'biblioteca' | 'repositorio' | null;

interface KeywordGroup {
  kind: DetectedKind;
  destination: SuggestedDestination;
  /** Mínimo de keywords distintas que devem aparecer no sample */
  minMatches: number;
  /** Lista de keywords (case-insensitive, match por substring) */
  keywords: readonly string[];
}

/**
 * Grupos de keywords por tipo de documento.
 *
 * Lista propositalmente CONSERVADORA — falso negativo é aceitável,
 * falso positivo é ruim porque sugere o destino errado ao usuário.
 *
 * Ordem importa: o primeiro grupo a satisfazer minMatches vence.
 * Por isso colocamos os tipos mais específicos primeiro.
 */
const KEYWORD_GROUPS: readonly KeywordGroup[] = [
  {
    kind: 'voto-judicial',
    destination: 'repositorio',
    minMatches: 2,
    keywords: [
      'voto',
      'acórdão',
      'acordao',
      'ementa',
      'ministro',
      'relator',
      'tribunal',
      'jurisprudência',
      'jurisprudencia',
      'recurso',
      'habeas corpus',
      'mandado de segurança',
      'mandado de seguranca',
      'agravo',
      'embargos',
      'apelação',
      'apelacao',
      'turma',
      'plenário',
      'plenario',
      'stf',
      'stj',
    ],
  },
  {
    kind: 'prontuario-medico',
    destination: 'repositorio',
    minMatches: 2,
    keywords: [
      'anamnese',
      'queixa principal',
      'evolução clínica',
      'evolucao clinica',
      'diagnóstico',
      'diagnostico',
      'paciente',
      'prontuário',
      'prontuario',
      'exame físico',
      'exame fisico',
      'cid-10',
      'cid 10',
      'conduta',
      'hipótese diagnóstica',
      'hipotese diagnostica',
      'sinais vitais',
      'medicação',
      'medicacao',
    ],
  },
  {
    kind: 'transcricao-entrevista',
    destination: 'repositorio',
    minMatches: 2,
    keywords: [
      'entrevistador',
      'entrevistado',
      'pergunta:',
      'resposta:',
      'transcrição',
      'transcricao',
      'gravação',
      'gravacao',
      '(inaudível)',
      '(inaudivel)',
      '(silêncio)',
      '(silencio)',
    ],
  },
];

export interface KindDetectionResult {
  kind: DetectedKind;
  destination: SuggestedDestination;
  matchedKeywords: string[];
}

const EMPTY_RESULT: KindDetectionResult = {
  kind: null,
  destination: null,
  matchedKeywords: [],
};

const SAMPLE_LENGTH = 1500;

/**
 * Analisa as primeiras chars do texto extraído e detecta se contém
 * keywords de algum dos kinds conhecidos.
 *
 * Retorna o PRIMEIRO grupo (em ordem de declaração) que satisfaz
 * minMatches, ou EMPTY_RESULT se nenhum.
 *
 * Performance: O(N*M) onde N = sample length e M = total de keywords (~50).
 * Para 1500 chars × 50 keywords = ~75k ops — sub-millisegundo.
 */
export function detectPdfKind(text: string | null | undefined): KindDetectionResult {
  if (!text || typeof text !== 'string' || text.length === 0) {
    return EMPTY_RESULT;
  }

  const sample = text.slice(0, SAMPLE_LENGTH).toLowerCase();

  for (const group of KEYWORD_GROUPS) {
    const matched = group.keywords.filter(kw => sample.includes(kw.toLowerCase()));
    if (matched.length >= group.minMatches) {
      return {
        kind: group.kind,
        destination: group.destination,
        matchedKeywords: matched,
      };
    }
  }

  return EMPTY_RESULT;
}

/**
 * Label human-readable do kind detectado, usado pelo BigwaStatusPanel
 * para construir a frase "Parece ser um {label}...".
 */
export function kindLabel(kind: DetectedKind): string {
  switch (kind) {
    case 'voto-judicial':
      return 'voto / acórdão / decisão judicial';
    case 'prontuario-medico':
      return 'prontuário médico';
    case 'transcricao-entrevista':
      return 'transcrição de entrevista';
    default:
      return 'documento';
  }
}
